/**
 * CR-7787: the Contract Owner dropdown shared by the create and edit contract
 * forms. The owner is required (as in the old portal), so there is no
 * "Not Assigned" choice; each state the list can be in says what it is waiting
 * for instead of showing an empty list.
 */
import { ContractOwnerOption } from '../../lib/api';

interface ContractOwnerSelectProps {
  value: string;
  onChange: (owner: string) => void;
  customerChosen: boolean;
  owners: ContractOwnerOption[];
  loading: boolean;
  error: string | null;
  /** The stored owner, offered even when no longer in the list, so an edit can keep them. */
  currentOwner?: { id: number; name: string } | null;
}

export default function ContractOwnerSelect({
  value,
  onChange,
  customerChosen,
  owners,
  loading,
  error,
  currentOwner,
}: ContractOwnerSelectProps) {
  const keepCurrent = currentOwner && !owners.some((o) => o.id === currentOwner.id);
  const empty = customerChosen && !loading && !error && owners.length === 0 && !keepCurrent;
  // Two people with the same name are told apart by their email.
  const nameCounts = owners.reduce<Record<string, number>>((acc, o) => {
    acc[o.name] = (acc[o.name] ?? 0) + 1;
    return acc;
  }, {});

  let placeholder = 'Select an owner';
  if (!customerChosen) placeholder = 'Choose a customer first';
  else if (loading) placeholder = 'Loading owners…';

  return (
    <>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={!customerChosen || loading}
        aria-invalid={Boolean(error)}
        className="w-full border rounded-lg px-3 py-2 text-sm disabled:bg-muted"
      >
        <option value="" disabled={customerChosen && !loading}>{placeholder}</option>
        {keepCurrent && (
          <option value={currentOwner.id}>{currentOwner.name} (current owner)</option>
        )}
        {owners.map((o) => (
          <option key={o.id} value={o.id}>
            {(nameCounts[o.name] ?? 0) > 1 ? `${o.name} (${o.email})` : o.name}
          </option>
        ))}
      </select>
      {error && <p className="text-xs text-destructive mt-1">{error}</p>}
      {empty && (
        <p className="text-xs text-muted-foreground mt-1">
          No one at this customer can own a contract yet. An owner needs an active
          account with a first name.
        </p>
      )}
    </>
  );
}
